import { useMemo } from 'react'

import useOfferList from 'hooks/Offers/useOfferList'
import { useAppSelector } from 'hooks/reduxHooks'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import Places from 'constants/places'
import config from 'constants/config'
import { FlightsClassTypes } from 'constants/flight'
import { useMaldivesDirectHotelStreamSearch } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesDirectHotelStreamSearch'
import { getMaldivesDirectSearchOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { getDefaultAirport } from 'selectors/flightsSelectors'

/** Enough offers for the homepage carousel without waiting on the full stream. */
const CAROUSEL_MIN_REQUIRED_OFFERS = 25

interface Props {
  excludedIds?: Array<string>
  /**
   * When true (homepage), use the anytime charter stream as the offer source with
   * server-side `price.asc` sort. Returned `bundleFilters` (including sort) are passed
   * to carousel tiles so ForceBundle pricing shares that same list key.
   * When false (Société), keep the light list only and omit `bundleFilters`; tile
   * pricing uses package + reward preview and must not depend on stream metadata.
   * Do not put `price.asc` on stream filters for Société / non-stream callers.
   */
  includeBundleStream?: boolean
}

interface MaldivesCharterOfferList extends App.OfferList {
  bundleFilters?: App.OfferListFilters
}

/**
 * Fetches Maldives charter flight bundle offers with pricing.
 * Homepage (`includeBundleStream`) uses the anytime stream with `price.asc` and returns
 * `bundleFilters` for tile pricing. Société keeps a light list and skips the stream.
 */
export default function useMaldivesCharterRecommendedOfferList({
  excludedIds,
  includeBundleStream = true,
}: Props): MaldivesCharterOfferList {
  const defaultRooms = useMemo(() => [config.search.defaultOccupants], [])
  // Homepage has no airport picker: normalise non-feeder defaults (e.g. PER) to MEL.
  // Maldives Direct landing keeps search origin equal to the selected departureAirport instead.
  const defaultAirport = useAppSelector(getDefaultAirport)
  const feederOriginAirportCode = useMemo(
    () => getMaldivesDirectSearchOriginAirportCode(defaultAirport),
    [defaultAirport],
  )

  // Light list for Société rewards / non-stream consumers
  const listFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_HOTEL],
    destinationId: Places.Maldives.id,
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    frontPageVisibleOnly: true,
    limit: CAROUSEL_MIN_REQUIRED_OFFERS,
  }), [])

  const { bundleFilters, canStreamHotelOffers } = useMaldivesDirectHotelStreamSearch({
    checkInDate: undefined,
    checkOutDate: undefined,
    rooms: defaultRooms,
    fareClassForSearch: FlightsClassTypes.ECONOMY,
    holidayTypes: undefined,
    amenities: undefined,
    // Only homepage returns these filters to tiles; Société must not build price.asc filters.
    sortBy: includeBundleStream ? 'price.asc' : undefined,
    isTourBundleMode: false,
    feederOriginAirportCode,
  })

  const lightOfferList = useOfferList(listFilters, {
    excludedIds,
    disabled: includeBundleStream,
  })

  const streamOfferList = useOfferList(bundleFilters, {
    disabled: !includeBundleStream || !canStreamHotelOffers,
    excludedIds,
    minRequiredOffers: CAROUSEL_MIN_REQUIRED_OFFERS,
  })

  if (!includeBundleStream) {
    return lightOfferList
  }

  return {
    ...streamOfferList,
    bundleFilters,
  }
}
